<template>
    <div v-if="pending">
        <div class="d-flex justify-content-center loading-center">
            <div class="spinner-border" role="status">
                <span class="sr-only">Loading...</span>
            </div>
        </div>
    </div>
	<div v-else class="container py-5">
        <h2>{{ $t('activityDetail') }}</h2>
        <nav aria-label="breadcrumb">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <RouterLink to="/modelings">
                        {{ $t('home') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="{ name: 'modeling', params: { id: this.$route.params.id } }">
                        {{ modelingName || $t('modelings') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">
                    <RouterLink :to="'/modeling/'+ this.$route.params.id + '/assettype/' + this.$route.params.assetTypeId" >
                        {{ assetTypeName || $t('assetType') }}
                    </RouterLink>
                </li>
                <li class="breadcrumb-item">{{ data.name }}</li>
            </ol>
        </nav>
        <div class="row">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="name" required>{{ $t('name') }}</label>
                <input v-model="data.name" type="text" class="form-control" id="name" >
            </div>
              <div class="mb-3 col-md-8">
                <label class="form-label" for="description" required>{{ $t('description') }}</label>
                <input v-model="data.description" type="text" class="form-control" id="description">
            </div>
        </div>
        <div class="row mb-3">
            <div class="mb-3 col-md-4">
                <label class="form-label" for="unit" required>{{ $t('unit') }}</label>
                <input v-model="data.unit" type="text" class="form-control" id="unit" >
            </div>
            <div class="mb-3 col-md-3">
                <label class="form-label" for="unitCost" required>{{ $t('unitCost') }}</label>
                <input v-model="data.unitCost" type="number" class="form-control" id="unitCost">
            </div>
            <div class="mb-3 col-md-2">
                <label class="form-label" for="activityColor" required>{{ $t('activityColor') }}</label>
                <input v-model="data.activityColor" type="color" class="form-control" id="activityColor">
            </div>
        </div>
        <label class="form-label" for="costFactor" required>{{ $t('costFactor') }}</label>
        <div class="row">
            <div class="mb-3 col-md-3" v-for="(costFactor, i) in costFactors" :key="i">
                <div class="input-group mb-3">
                    <select :id="'costFactor_' + i" class="form-select" v-model="costFactors[i]" >
                        <option disabled>{{ $t('select') }}</option>
                        <template v-if="datasetHeaders.length > 0" >
                            <option v-for="(datasetHeader, i) in datasetHeaders" :key="i" v-bind:value="datasetHeader.name">{{ datasetHeader.alias }}</option>
                        </template>
                        <template v-if="missingDatasetHeaders.length > 0" >
                            <option v-for="(missingDatasetHeader, i) in missingDatasetHeaders" :key="i" disabled  v-bind:value="missingDatasetHeader.name">{{ missingDatasetHeader.alias }}</option>
                        </template>
                    </select>
                    <span class="input-group-text" id="basic-addon2"><i class=" fa fa-remove" @click="removeCostFactor(i)" /></span>
                </div>
            </div>
            <div class="mb-3 col-md-3">
                <button class="form-control btn btn-light" id="unit" @click="addCostFactor" >{{$t('addCostFactor')}}</button>
            </div>
        </div>
        <div class="row mb-3">
            <div class="mb-3 col-md-6">
                <label class="form-check-label" for="rootActivity">
                    {{ $t('rootActivity') }}
                </label>
                <div class="input-group">
                    <div class="input-group-text">
                        <input class="form-check-input mt-0" v-model="data.rootActivity" type="checkbox" id="rootActivity">
                    </div>
                    <input v-bind:disabled="!data.rootActivity" v-model="data.rootActivityEffect" type="number" class="form-control" id="rootActivityEffect">
                    <select v-bind:disabled="!data.rootActivity" class="form-select" v-model="data.rootActivityEffectType" >
                        <option value="0">{{ $t('Absolute') }}</option>
                        <option value="1">{{ $t('Relative') }}</option>
                        <option value="2">{{ $t('Differential') }}</option>
                    </select>
                </div>
            </div>
        </div>
        <div class="d-grid gap-2 d-md-flex ">
            <template v-if="isUserAdmin">
                <button type="button" class="btn btn-primary" @click="onSubmit">{{ $t('save') }}</button>
                <button type="button" class="btn btn-danger" @click="showDeleteActivity">{{ $t('delete') }}</button>
            </template>
            <button type="button" class="btn btn-secondary" @click="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })">{{ $t('back') }}</button>
        </div>
        <ActivityDeleteModal v-if="showActivityDeleteModal" @reload="$router.push({ path: `/modeling/${this.$route.params.id}/assettype/${this.$route.params.assetTypeId}` })" @closeDeleteDialog="showActivityDeleteModal = false"/>
    </div>
</template>

<script>
import ActivityApi from "../../api/server/activity";
import DatasetApi from "../../api/server/dataset";
import AssetTypeApi from "../../api/server/assetType";
import ActivityDeleteModal from '../activities/ActivityDeleteModal.vue';
import ModelingApi from "../../api/server/modeling";
import { CheckIfUserIsEditor } from '../../authCache';

export default {
    name: "activitydetail",
    watch: {
        $route: "requestData",
    },
    components: {
        ActivityDeleteModal,
    },
    mounted() {
        this.requestData();
    },
    methods: {
        async requestData() {
            this.pending = true;
            this.isUserAdmin = await CheckIfUserIsEditor();

            var modelingInfo = await ModelingApi.modelingInfo(this.$route.params.id);
            var foundAssetType = modelingInfo.assetTypes.find(a => a.id == this.$route.params.assetTypeId);
            if(foundAssetType != null) {
                for(var i in foundAssetType.lifeCycles) {
                    var lifeCycle = foundAssetType.lifeCycles[i];
                    for(var o in lifeCycle.lifeCycleActivities) {
                        var activity = lifeCycle.lifeCycleActivities[o];
                        
                        if(activity.activityId == this.$route.params.activityId) {
                            this.foundActivities.push({ 
                                lifeCycleId: lifeCycle.id,
                                activityId: activity.activityId,
                                name: "LifeCycle - " + lifeCycle.name + " - " + activity.activityName
                            });
                        }
                    }
                }
                for(var i in foundAssetType.parameters) {
                    var parameter = foundAssetType.parameters[i];
                    for(var o in parameter.curves) {
                        var curve = parameter.curves[o];
                        for(var u in curve.effects) {
                            var effect = curve.effects[u];
                            if(effect.activityId == this.$route.params.activityId) {
                                this.foundActivities.push({ 
                                    curveId: curve.id,
                                    activityId: effect.activityId,
                                    type: "curve",
                                    name: "Parameter - " + parameter.name + " - Curve - " + curve.name
                                });
                            }
                        }
                    }
                }
            }
            this.data = await ActivityApi.activityDetail(this.$route.params.id, this.$route.params.activityId);
            if(this.data.costFactor)
                this.costFactors = this.data.costFactor.split(';');

            var assetType = await AssetTypeApi.assetTypeDetail(this.$route.params.id, this.$route.params.assetTypeId);
            this.assetTypeName = assetType.name;

            var dataset = await DatasetApi.datasetDetail(this.$route.params.id, assetType.datasetId);
            var datasetContent = JSON.parse(dataset.content);
            this.datasetHeaders = datasetContent["headers"];

            var modeling = await ModelingApi.modelingDetail(this.$route.params.id);
            this.modelingName = modeling.name;

            for(var i in this.costFactors) {
                var attr = this.costFactors[i];
                if(this.datasetHeaders.findIndex(d => d.name == attr) < 0 && this.missingDatasetHeaders.findIndex(d => d.name == attr) < 0) {
                    this.missingDatasetHeaders.push({ name: attr, alias: attr });
                }
            }

            this.pending = false;
        },
        async onSubmit() {
            if(this.data.name.length > 0) {
                if(this.costFactors.length > 1)
                    this.data.costFactor = this.costFactors.join(";");
                else 
                    this.data.costFactor = this.costFactors.join("");
                
                await ActivityApi.activityUpdate(this.$route.params.id, this.data);
                if(!this.isFormValid())
                    this.showWarning();
                else 
                    this.showSuccess();
            } else {
                this.showEmptyNameError();
            }
        },
        isFormValid() {
            for(var i in this.costFactors) {
                var attr = this.costFactors[i];
                if(this.datasetHeaders.findIndex(d => d.name == attr) < 0 && attr != "")
                    return false;
            }

            return true
        },
        showDeleteActivity() {
            this.activityToDeleteId = this.data.id;
            this.showActivityDeleteModal = true;
        },
        addCostFactor() {
            this.costFactors.push({  
                name: ""
            })
        },
        removeCostFactor(i) {
            this.costFactors.splice(i, 1);
        }
    },
    notifications: {
        showSuccess: { 
            title: 'success',
            message: 'activityUpdated',
            type: 'success'
        },
        showWarning: {
            title: 'warning',
            message: 'warningAttributeRemoved',
            type: 'warn'
        },
        showEmptyNameError: { 
            title: 'error',
            message: 'emptyName',
            type: 'error'
        }
    },
    data() {
        return {
            costFactors: [],
            missingDatasetHeaders: [],
            datasetHeaders: [],
            data: {
                name: "",
                id: ""
            },
            showActivityDeleteModal: false,
            activityToDeleteId: 0,
            modelingName: null,
            assetTypeName: null,
            foundActivities: [],
            pending: true,
            isUserAdmin: false
        }
    }
};
</script>